import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { iniciativasAPI, areasAPI, personasAPI, negociosAPI } from '../services/api';
import { Stepper, Step, StepLabel, Button as MUIButton, Box, FormControl, InputLabel, Select, MenuItem, Autocomplete, TextField } from '@mui/material';

const CrearOportunidad = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [areas, setAreas] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [iniciativas, setIniciativas] = useState([]);
  const [lineasNegocio, setLineasNegocio] = useState([]);
  const [negociosFiltrados, setNegociosFiltrados] = useState([]);
  const [subnegociosFiltrados, setSubnegociosFiltrados] = useState([]);

  // Detectar origen para título SOLO al montar
  const [origen, setOrigen] = useState('');
  const [tituloFormulario, setTituloFormulario] = useState('Crear Nueva Oportunidad');

  useEffect(() => {
    let detected = '';
    if (location.state && location.state.from) {
      if (location.state.from.includes('operaciones')) detected = 'operaciones';
      else if (location.state.from.includes('desarrollo')) detected = 'desarrollo';
    } else if (document.referrer) {
      if (document.referrer.includes('operaciones')) detected = 'operaciones';
      else if (document.referrer.includes('desarrollo')) detected = 'desarrollo';
    }
    setOrigen(detected);
    setTituloFormulario(detected === 'operaciones' ? 'Crear Nuevo Proyecto' : 'Crear Nueva Oportunidad');
  }, [location.state]);

  const [formData, setFormData] = useState({
    nombre_de_la_oportunidad: '',
    descripcion: '',
    linea_de_negocio: '',
    negocio: '',
    subnegocio: '',
    negocio_estructura: '',
    contacto_de_negocio: '',
    estado: 'oportunidad_identificada',
    tecnologia: '',
    area_ejecucion: '',
    valor_minimo_estimado: '',
    valor_maximo_estimado: '',
    duracion_minima_estimada: '',
    duracion_maxima_estimada: '',
    fecha_de_entrega_propuesta_prevista: '',
    fecha_de_fin_planificada: '',
    año: '',
    madurez: '',
    revision_dn: false,
    tipo_de_iniciativa: 'iniciativa',
    relevancia_para_el_dh: false,
    productivizado: false,
    enlace_al_documento_ppt_sharepoint: '',
    enlace_a_la_pagina_de_confluence: '',
    importe_total_estimado: '',
    importe_total_cliente: '',
    probabilidad_de_aprobacion: '',
    revision_lt: false,
    fecha_de_aprobacion_propuesta_firmada: '',
    responsable_comercial_id: '',
    lider_tecnico_id: '',
    padre: ''
  });

  // Cargar datos de referencia al montar el componente
  useEffect(() => {
    cargarDatosReferencia();
  }, []);

  // Efecto para procesar las líneas de negocio únicas
  useEffect(() => {
    if (negocios && negocios.length > 0) {
      const lineasUnicas = [...new Set(negocios.map(n => n.linea_de_negocio))].sort();
      setLineasNegocio(lineasUnicas);
    }
  }, [negocios]);

  // Efecto para obtener todos los negocios únicos (sin filtrar)
  useEffect(() => {
    if (negocios && negocios.length > 0) {
      const negociosUnicos = [...new Set(negocios.map(n => n.negocio))].sort();
      setNegociosFiltrados(negociosUnicos);
    }
  }, [negocios]);

  // Efecto para obtener todos los subnegocios únicos (sin filtrar)
  useEffect(() => {
    if (negocios && negocios.length > 0) {
      const subnegociosUnicos = negocios
        .map(n => ({ id: n.id, subnegocio: n.subnegocio, negocio: n.negocio, linea_de_negocio: n.linea_de_negocio }))
        .sort((a, b) => a.subnegocio.localeCompare(b.subnegocio));
      setSubnegociosFiltrados(subnegociosUnicos);
    }
  }, [negocios]);

  const cargarDatosReferencia = async () => {
    try {
      console.log('Cargando datos de referencia...');

      const [areasResponse, personasResponse, negociosResponse, iniciativasResponse] = await Promise.all([
        areasAPI.getAll(),
        personasAPI.getAll(),
        negociosAPI.getAll(),
        iniciativasAPI.getAll()
      ]);

      const areasData = Array.isArray(areasResponse.data) ? areasResponse.data : areasResponse.data?.results || [];
      const personasData = Array.isArray(personasResponse.data) ? personasResponse.data : personasResponse.data?.results || [];
      const negociosData = Array.isArray(negociosResponse.data) ? negociosResponse.data : negociosResponse.data?.results || [];
      const iniciativasData = Array.isArray(iniciativasResponse.data) ? iniciativasResponse.data : iniciativasResponse.data?.results || [];

      setAreas(areasData);
      setPersonas(personasData);
      setNegocios(negociosData);
      setIniciativas(iniciativasData);
    } catch (err) {
      console.error('Error cargando datos de referencia:', err);
      setError('Error al cargar los datos de referencia');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Manejar combos relacionales de negocio
    if (name === 'subnegocio') {
      // Encontrar el negocio completo por subnegocio
      const negocioCompleto = negocios.find(n => n.subnegocio === value);

      if (negocioCompleto) {
        setFormData(prev => ({
          ...prev,
          subnegocio: value,
          negocio: negocioCompleto.negocio,
          linea_de_negocio: negocioCompleto.linea_de_negocio,
          negocio_estructura: negocioCompleto.id
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          subnegocio: value,
          negocio_estructura: ''
        }));
      }
      return;
    }

    if (name === 'negocio') {
      // Encontrar la línea de negocio por negocio
      const negocioCompleto = negocios.find(n => n.negocio === value);

      if (negocioCompleto) {
        setFormData(prev => ({
          ...prev,
          negocio: value,
          linea_de_negocio: negocioCompleto.linea_de_negocio,
          subnegocio: '', // Limpiar subnegocio al cambiar negocio
          negocio_estructura: ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          negocio: value,
          subnegocio: '',
          negocio_estructura: ''
        }));
      }
      return;
    }

    if (name === 'linea_de_negocio') {
      // Al cambiar línea de negocio, limpiar negocio y subnegocio
      setFormData(prev => ({
        ...prev,
        linea_de_negocio: value,
        negocio: '',
        subnegocio: '',
        negocio_estructura: ''
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Preparar datos para envío
      const dataToSend = {
        ...formData,
        // Convertir strings vacíos a null para campos numéricos
        valor_minimo_estimado: formData.valor_minimo_estimado || null,
        valor_maximo_estimado: formData.valor_maximo_estimado || null,
        duracion_minima_estimada: formData.duracion_minima_estimada || null,
        duracion_maxima_estimada: formData.duracion_maxima_estimada || null,
        importe_total_estimado: formData.importe_total_estimado || null,
        importe_total_cliente: formData.importe_total_cliente || null,
        tecnologia: formData.tecnologia || null,
        area_ejecucion: formData.area_ejecucion || null,
        negocio_estructura: formData.negocio_estructura || null,
        // Fechas vacías se convierten a null
        fecha_de_entrega_propuesta_prevista: formData.fecha_de_entrega_propuesta_prevista || null,
        fecha_de_fin_planificada: formData.fecha_de_fin_planificada || null,
        fecha_de_aprobacion_propuesta_firmada: formData.fecha_de_aprobacion_propuesta_firmada || null,
        año: formData.año || null,
        madurez: formData.madurez || null,
        probabilidad_de_aprobacion: formData.probabilidad_de_aprobacion || null,
        padre: formData.padre || null
      };

      // Crear la iniciativa
      const response = await iniciativasAPI.create(dataToSend);

      // Si se especificaron responsables, agregarlos
      if (formData.responsable_comercial_id) {
        await iniciativasAPI.agregarPersona(response.data.id, {
          persona_id: formData.responsable_comercial_id,
          rol: 'responsable_comercial'
        });
      }

      if (formData.lider_tecnico_id) {
        await iniciativasAPI.agregarPersona(response.data.id, {
          persona_id: formData.lider_tecnico_id,
          rol: 'lider_tecnico'
        });
      }

      // Mostrar mensaje de éxito
      setSuccess(true);

      // Redireccionar después de un tiempo
      setTimeout(() => {
        navigate('/tabla-desarrollo-negocio');
      }, 2000);

    } catch (err) {
      console.error('Error creando iniciativa:', err);
      setError('Error al crear la iniciativa. Por favor, revise los datos e intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Si hay información de navegación previa, úsala
    if (location.state && location.state.from) {
      navigate(location.state.from);
      return;
    }
    // Heurística: si la URL previa contiene "operaciones" ir a Operaciones, si no a Desarrollo de Negocio
    const referrer = document.referrer || '';
    if (referrer.includes('operaciones')) {
      navigate('/operaciones');
    } else {
      navigate('/desarrollo-negocio');
    }
  };


  // Agrupación simplificada de pasos
  const steps = [
    'Información Básica',
    'Estimaciones y Fechas',
    'Enlaces y Opciones'
  ];

  const [activeStep, setActiveStep] = useState(0);

  // Renderizado de campos por paso
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div className="row g-4">
            {/* Card 1: Definición */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">📋 Definición</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {/* Nombre de la oportunidad */}
                    <div className="col-12">
                      <label className="form-label"><strong>Nombre de la oportunidad *</strong></label>
                      <input
                        type="text"
                        className="form-control"
                        name="nombre_de_la_oportunidad"
                        value={formData.nombre_de_la_oportunidad}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    {/* Descripción */}
                    <div className="col-12">
                      <label className="form-label">Descripción</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        name="descripcion"
                        value={formData.descripcion}
                        onChange={handleInputChange}
                      ></textarea>
                    </div>
                    {/* Tipo de iniciativa y Estado */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Tipo de iniciativa</label>
                      <select
                        className="form-control"
                        name="tipo_de_iniciativa"
                        value={formData.tipo_de_iniciativa}
                        onChange={handleInputChange}
                      >
                        <option value="iniciativa">Iniciativa</option>
                        <option value="servicio">Servicio</option>
                        <option value="mantenimiento">Mantenimiento</option>
                        <option value="programa">Programa</option>
                      </select>
                    </div>
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Estado</label>
                      <select
                        className="form-control"
                        name="estado"
                        value={formData.estado}
                        onChange={handleInputChange}
                      >
                        <option value="oportunidad_identificada">Oportunidad identificada</option>
                        <option value="en_preparacion_propuesta">En preparación propuesta</option>
                        <option value="supervision">Supervisión</option>
                        <option value="entregada">Entregada</option>
                        <option value="rechazada">Rechazada</option>
                        <option value="stand_by">Stand-By</option>
                      </select>
                    </div>
                    {/* Iniciativa Padre */}
                    <div className="col-12">
                      <label className="form-label">Iniciativa Padre (opcional)</label>
                      <Autocomplete
                        options={[
                          { id: '', nombre_de_la_oportunidad: 'Ninguna (Iniciativa principal)', tipo_de_iniciativa: '' },
                          ...iniciativas
                            .filter(i => i.nombre_de_la_oportunidad)
                            .sort((a, b) => {
                              const nombreA = a.nombre_de_la_oportunidad || '';
                              const nombreB = b.nombre_de_la_oportunidad || '';
                              return nombreA.localeCompare(nombreB);
                            })
                        ]}
                        getOptionLabel={(option) => {
                          if (!option.id) return option.nombre_de_la_oportunidad;
                          return `${option.nombre_de_la_oportunidad}${option.tipo_de_iniciativa ? ` [${option.tipo_de_iniciativa}]` : ''}`;
                        }}
                        value={iniciativas.find(i => i.id === formData.padre) || { id: '', nombre_de_la_oportunidad: 'Ninguna (Iniciativa principal)', tipo_de_iniciativa: '' }}
                        onChange={(event, newValue) => {
                          setFormData(prev => ({
                            ...prev,
                            padre: newValue ? newValue.id : ''
                          }));
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Buscar iniciativa..."
                            variant="outlined"
                            size="small"
                          />
                        )}
                        noOptionsText="No se encontraron iniciativas"
                        clearText="Limpiar"
                        openText="Abrir"
                        closeText="Cerrar"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            padding: '4px',
                          }
                        }}
                      />
                      <div className="form-text">Si esta iniciativa depende de otra, selecciónela aquí. ({iniciativas.length} iniciativas disponibles)</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Información de Negocio */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">🏢 Información de Negocio</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {/* Línea de negocio */}
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label">Línea de negocio</label>
                      <select
                        className="form-control"
                        name="linea_de_negocio"
                        value={formData.linea_de_negocio}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar línea...</option>
                        {lineasNegocio.map(linea => (
                          <option key={linea} value={linea}>{linea}</option>
                        ))}
                      </select>
                    </div>
                    {/* Negocio */}
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label">Negocio</label>
                      <select
                        className="form-control"
                        name="negocio"
                        value={formData.negocio}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar negocio...</option>
                        {negociosFiltrados.map(negocio => (
                          <option key={negocio} value={negocio}>{negocio}</option>
                        ))}
                      </select>
                    </div>
                    {/* Subnegocio */}
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label"><strong>Subnegocio *</strong></label>
                      <select
                        className="form-control"
                        name="subnegocio"
                        value={formData.subnegocio}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar subnegocio...</option>
                        {subnegociosFiltrados.map(item => (
                          <option key={item.id} value={item.subnegocio}>{item.subnegocio}</option>
                        ))}
                      </select>
                    </div>
                    {/* Contacto de negocio */}
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label">Contacto de negocio</label>
                      <input
                        type="text"
                        className="form-control"
                        name="contacto_de_negocio"
                        value={formData.contacto_de_negocio}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3: Digital Hub */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">💻 Digital Hub</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {/* Área de conocimiento */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Área de conocimiento</label>
                      <select
                        className="form-control"
                        name="tecnologia"
                        value={formData.tecnologia}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar área...</option>
                        {Array.isArray(areas) && areas.map(area => (
                          <option key={area.id} value={area.id}>{area.nombre}</option>
                        ))}
                      </select>
                    </div>
                    {/* Área de ejecución */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Área de ejecución</label>
                      <select
                        className="form-control"
                        name="area_ejecucion"
                        value={formData.area_ejecucion}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar área...</option>
                        {Array.isArray(areas) && areas.map(area => (
                          <option key={area.id} value={area.id}>{area.nombre}</option>
                        ))}
                      </select>
                    </div>
                    {/* Responsable DN */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label"><strong>Responsable DN *</strong></label>
                      <select
                        className="form-control"
                        name="responsable_comercial_id"
                        value={formData.responsable_comercial_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar persona...</option>
                        {Array.isArray(personas) && personas.map(persona => (
                          <option key={persona.id} value={persona.id}>
                            {persona.nombre} {persona.apellido}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Líder técnico */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Líder técnico</label>
                      <select
                        className="form-control"
                        name="lider_tecnico_id"
                        value={formData.lider_tecnico_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar persona...</option>
                        {Array.isArray(personas) && personas.map(persona => (
                          <option key={persona.id} value={persona.id}>
                            {persona.nombre} {persona.apellido}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="row g-4">
            {/* Card 1: Estimaciones */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">📊 Estimaciones</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {/* Valores estimados */}
                    <div className="col-lg-3 col-md-6">
                      <label className="form-label">Valor mínimo estimado (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="valor_minimo_estimado"
                        value={formData.valor_minimo_estimado}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <label className="form-label">Valor máximo estimado (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="valor_maximo_estimado"
                        value={formData.valor_maximo_estimado}
                        onChange={handleInputChange}
                      />
                    </div>
                    {/* Duraciones */}
                    <div className="col-lg-3 col-md-6">
                      <label className="form-label">Duración mínima estimada (días)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="duracion_minima_estimada"
                        value={formData.duracion_minima_estimada}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <label className="form-label">Duración máxima estimada (días)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="duracion_maxima_estimada"
                        value={formData.duracion_maxima_estimada}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Fechas */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">📅 Fechas</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label">Fecha de entrega propuesta (prevista)</label>
                      <input
                        type="date"
                        className="form-control"
                        name="fecha_de_entrega_propuesta_prevista"
                        value={formData.fecha_de_entrega_propuesta_prevista}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label">Fecha de fin planificada</label>
                      <input
                        type="date"
                        className="form-control"
                        name="fecha_de_fin_planificada"
                        value={formData.fecha_de_fin_planificada}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-lg-4 col-md-12">
                      <label className="form-label">Fecha de aprobación/propuesta firmada</label>
                      <input
                        type="date"
                        className="form-control"
                        name="fecha_de_aprobacion_propuesta_firmada"
                        value={formData.fecha_de_aprobacion_propuesta_firmada}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Año</label>
                      <input
                        type="number"
                        className="form-control"
                        name="año"
                        value={formData.año}
                        onChange={handleInputChange}
                        min="2020"
                        max="2030"
                        placeholder="Ej: 2024"
                      />
                    </div>
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Madurez</label>
                      <select
                        className="form-control"
                        name="madurez"
                        value={formData.madurez}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar madurez...</option>
                        <option value="poc">POC (Proof of Concept)</option>
                        <option value="piloto">Piloto</option>
                        <option value="proyecto">Proyecto</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3: Finanzas */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">💰 Finanzas</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label">Importe total estimado (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="importe_total_estimado"
                        value={formData.importe_total_estimado}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label">Importe de Venta (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        name="importe_total_cliente"
                        value={formData.importe_total_cliente}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-lg-4 col-md-12">
                      <label className="form-label">Probabilidad de aprobación</label>
                      <select
                        className="form-control"
                        name="probabilidad_de_aprobacion"
                        value={formData.probabilidad_de_aprobacion}
                        onChange={handleInputChange}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="alta">Alta</option>
                        <option value="media">Media</option>
                        <option value="baja">Baja</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="row g-4">
            {/* Card 1: Enlaces */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">🔗 Enlaces</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Enlace al documento (PPT - Sharepoint)</label>
                      <input
                        type="url"
                        className="form-control"
                        name="enlace_al_documento_ppt_sharepoint"
                        value={formData.enlace_al_documento_ppt_sharepoint}
                        onChange={handleInputChange}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label">Enlace a la página de Confluence</label>
                      <input
                        type="url"
                        className="form-control"
                        name="enlace_a_la_pagina_de_confluence"
                        value={formData.enlace_a_la_pagina_de_confluence}
                        onChange={handleInputChange}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Opciones */}
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0 text-primary">⚙️ Opciones</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-lg-3 col-md-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="relevancia_para_el_dh"
                          checked={formData.relevancia_para_el_dh}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">Relevancia para el DH</label>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="revision_lt"
                          checked={formData.revision_lt}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">Revisión LT</label>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="revision_dn"
                          checked={formData.revision_dn}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">En revisión de negocio</label>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="productivizado"
                          checked={formData.productivizado}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">Productivizado</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (success) {
    return (
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="alert alert-success text-center" role="alert">
              <i className="fas fa-check-circle fa-3x mb-3"></i>
              <h4 className="alert-heading">¡Oportunidad guardada exitosamente!</h4>
              <p>La oportunidad ha sido registrada en el sistema.</p>
              <hr />
              <p className="mb-0">Redirigiendo a la tabla de desarrollo de negocio...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-0">
                {/* <i className="fas fa-plus text-success me-2"></i> */}
                {tituloFormulario}
              </h2>
              <p className="text-muted mb-0">Complete el formulario para registrar una nueva {origen === 'operaciones' ? 'proyecto' : 'oportunidad'} de negocio. Puede guardar en cualquier paso y editar más adelante.</p>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleCancel}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Volver
            </button>
          </div>

          {/* Mensajes de error */}
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {/* Stepper y formulario paginado */}
          <div className="card">
            <div className="card-body">
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label, idx) => (
                  <Step key={label}>
                    <StepLabel
                      style={{ cursor: idx <= activeStep ? 'pointer' : 'default' }}
                      onClick={() => {
                        if (idx <= activeStep) setActiveStep(idx);
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              <form onSubmit={handleSubmit}>
                <Box sx={{ mt: 4 }}>{renderStepContent(activeStep)}</Box>
                <div className="d-flex justify-content-between mt-5">
                  <MUIButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => setActiveStep((prev) => prev - 1)}
                    disabled={activeStep === 0}
                  >
                    Anterior
                  </MUIButton>
                  <div>
                    {activeStep < steps.length - 1 && (
                      <MUIButton
                        variant="contained"
                        color="primary"
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveStep((prev) => prev + 1);
                        }}
                        sx={{ mr: 2 }}
                      >
                        Siguiente
                      </MUIButton>
                    )}
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={loading || !formData.nombre_de_la_oportunidad || !formData.subnegocio || !formData.responsable_comercial_id}
                      title={
                        loading ? "Guardando..." :
                        (() => {
                          const camposFaltantes = [];
                          if (!formData.nombre_de_la_oportunidad) camposFaltantes.push("Nombre de la oportunidad");
                          if (!formData.subnegocio) camposFaltantes.push("Subnegocio");
                          if (!formData.responsable_comercial_id) camposFaltantes.push("Responsable DN");
                          return camposFaltantes.length > 0 
                            ? `Campos obligatorios faltantes:\n${camposFaltantes.map(c => `• ${c}`).join('\n')}`
                            : "";
                        })()
                      }
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Guardando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Guardar Oportunidad
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrearOportunidad;